/**
 * Google Analytics service for CanvasEstate
 * Provides custom event tracking for financial app specific actions
 * Uses Firebase Analytics SDK for better integration and offline support
 */

import { logEvent, setUserProperties, type Analytics } from 'firebase/analytics';
import { analytics as firebaseAnalytics } from './firebase';
import { adminService } from './AdminService';

export interface AnalyticsEvent {
  action: string;
  category: string;
  label?: string;
  value?: number;
  customParameters?: Record<string, any>;
}

export class AnalyticsService {
  private static instance: AnalyticsService;
  private analytics: Analytics | null = null;
  private isEnabled: boolean = false;

  private constructor() {
    // Enable by default if we are in a browser
    this.isEnabled = typeof window !== 'undefined';
    this.checkAnalytics();
  }

  private checkAnalytics() {
    // Analytics initialization is async, so we check periodically
    const maxAttempts = 10;
    let attempts = 0;

    const checkInterval = setInterval(() => {
      if (firebaseAnalytics) {
        this.analytics = firebaseAnalytics;
        this.isEnabled = true;
        console.log('✅ Analytics service enabled with Firebase SDK');
        clearInterval(checkInterval);
      } else if (++attempts >= maxAttempts) {
        console.warn('⚠️ Analytics not available after max attempts');
        clearInterval(checkInterval);
      }
    }, 500);
  }

  public static getInstance(): AnalyticsService {
    if (!AnalyticsService.instance) {
      AnalyticsService.instance = new AnalyticsService();
    }
    return AnalyticsService.instance;
  }

  /**
   * Track a custom event
   */
  public trackEvent(event: AnalyticsEvent): void {
    if (!this.isEnabled || !this.analytics) {
      console.log('Analytics not available:', event);
      return;
    }

    try {
      logEvent(this.analytics, event.action, {
        event_category: event.category,
        event_label: event.label,
        value: event.value,
        ...event.customParameters
      });
    } catch (error) {
      console.warn('Analytics tracking error:', error);
    }
  }

  /**
   * Track page view
   */
  public trackPageView(pagePath: string, pageTitle?: string): void {
    if (!this.isEnabled || !this.analytics) {
      console.log('Analytics not available for page view:', pagePath);
      return;
    }

    try {
      logEvent(this.analytics, 'page_view', {
        page_path: pagePath,
        page_title: pageTitle
      });

      // Also log to admin dashboard
      adminService.logActivity('Page View', `Visited ${pageTitle || pagePath}`, { path: pagePath });
    } catch (error) {
      console.warn('Analytics page view tracking error:', error);
    }
  }

  /**
   * Track portfolio-specific events
   */
  public trackPortfolioEvent(action: string, details: {
    propertyCount?: number;
    totalValue?: number;
    propertyType?: string;
    location?: string;
  }): void {
    this.trackEvent({
      action: action,
      category: 'Portfolio Management',
      customParameters: {
        portfolio_action: action,
        property_count: details.propertyCount,
        total_value: details.totalValue,
        property_type: details.propertyType,
        location: details.location
      }
    });
  }

  /**
   * Track property management events
   */
  public trackPropertyEvent(action: string, details: {
    propertyId?: string;
    address?: string;
    propertyType?: string;
    location?: string;
    value?: number;
  }): void {
    this.trackEvent({
      action: action,
      category: 'Property Management',
      label: details.address || details.propertyId,
      value: details.value,
      customParameters: {
        property_id: details.propertyId,
        property_type: details.propertyType,
        location: details.location
      }
    });
  }

  /**
   * Track financial calculation events
   */
  public trackFinancialCalculation(calculationType: string, details: {
    result?: number;
    inputParameters?: Record<string, any>;
  }): void {
    this.trackEvent({
      action: 'financial_calculation',
      category: 'Financial Tools',
      label: calculationType,
      value: details.result,
      customParameters: {
        calculation_type: calculationType,
        ...details.inputParameters
      }
    });
  }

  /**
   * Track user authentication events
   */
  public trackAuthEvent(action: 'login' | 'logout' | 'signup', method?: string): void {
    this.trackEvent({
      action: action,
      category: 'Authentication',
      label: method,
      customParameters: {
        auth_method: method
      }
    });
  }

  /**
   * Track authentication storage issues (IndexedDB, persistence failures)
   * Use this to monitor how often users experience storage-related auth problems
   */
  public trackAuthStorageIssue(issueType: 'auth_timeout' | 'persistence_fallback' | 'indexeddb_error', details: {
    elapsedMs?: number;
    errorMessage?: string;
    fallbackType?: string;
  }): void {
    this.trackEvent({
      action: 'auth_storage_issue',
      category: 'Authentication Issues',
      label: issueType,
      value: details.elapsedMs,
      customParameters: {
        issue_type: issueType,
        elapsed_ms: details.elapsedMs,
        error_message: details.errorMessage?.substring(0, 100), // Truncate long errors
        fallback_type: details.fallbackType
      }
    });
  }

  /**
   * Track AI advice interactions
   */
  public trackAIAdvice(action: string, details: {
    adviceType?: string;
    portfolioValue?: number;
    success?: boolean;
  }): void {
    this.trackEvent({
      action: action,
      category: 'AI Financial Advice',
      label: details.adviceType,
      customParameters: {
        advice_type: details.adviceType,
        portfolio_value: details.portfolioValue,
        success: details.success
      }
    });
  }

  /**
   * Track debt payoff planning events
   */
  public trackDebtPayoffEvent(action: string, details: {
    totalDebt?: number;
    payoffStrategy?: string;
    timeframe?: number;
  }): void {
    this.trackEvent({
      action: action,
      category: 'Debt Management',
      customParameters: {
        total_debt: details.totalDebt,
        payoff_strategy: details.payoffStrategy,
        timeframe_months: details.timeframe
      }
    });
  }

  /**
   * Track specific UI interactions (buttons, modals, etc.)
   */
  public trackUIInteraction(elementName: string, action: string, context?: string): void {
    this.trackEvent({
      action: `ui_${action}`,
      category: 'UI Interaction',
      label: elementName,
      customParameters: {
        element_name: elementName,
        interaction_type: action,
        context: context
      }
    });

    // Also log key interactions to admin if appropriate
    const highValueActions = ['Add Property', 'Delete Property', 'Run Projection', 'Sync Profile'];
    if (highValueActions.includes(elementName) || action === 'submit') {
      adminService.logActivity(`UI ${action}`, `${elementName} in ${context || 'app'}`, { context });
    }
  }

  /**
   * Log high-value user insights directly to admin console
   */
  public logAdminInsight(action: string, details: string, metadata?: Record<string, any>): void {
    adminService.logActivity(action, details, metadata);
  }

  /**
   * Track session engagement time
   */
  public trackSessionTime(pagePath: string, durationSeconds: number): void {
    this.trackEvent({
      action: 'session_duration',
      category: 'Engagement',
      label: pagePath,
      value: durationSeconds,
      customParameters: {
        page_path: pagePath,
        duration_seconds: durationSeconds
      }
    });
  }

  /**
   * Track conversion funnel steps
   */
  public trackFunnelStep(funnelName: string, stepName: string, stepNumber: number, details?: Record<string, any>): void {
    this.trackEvent({
      action: 'funnel_step',
      category: 'Funnel',
      label: `${funnelName} - ${stepName}`,
      value: stepNumber,
      customParameters: {
        funnel_name: funnelName,
        step_name: stepName,
        step_number: stepNumber,
        ...details
      }
    });
  }

  /**
   * Track AI feedback (thumbs up/down)
   */
  public trackAIFeedback(adviceId: string, feedback: 'helpful' | 'unhelpful', comment?: string): void {
    this.trackEvent({
      action: 'ai_feedback',
      category: 'AI Quality',
      label: feedback,
      customParameters: {
        advice_id: adviceId,
        feedback_type: feedback,
        comment: comment
      }
    });
    
    // Log to admin for immediate visibility
    this.logAdminInsight('AI Feedback', `User rated advice as ${feedback}: ${comment || 'No comment'}`, { adviceId });
  }

  /**
   * Track runtime errors and failures
   */
  public trackError(errorType: string, message: string, stack?: string, fatal: boolean = false): void {
    this.trackEvent({
      action: 'app_error',
      category: 'Error Tracking',
      label: errorType,
      customParameters: {
        error_message: message.substring(0, 500),
        error_type: errorType,
        is_fatal: fatal,
        stack_trace: stack?.substring(0, 1000)
      }
    });

    if (fatal) {
      this.logAdminInsight('Fatal Error', `System error: ${message}`, { errorType, fatal });
    }
  }

  /**
   * Capture device and browser characteristics
   */
  public captureDeviceInfo(): void {
    if (typeof window === 'undefined') return;

    const deviceInfo = {
      screen_resolution: `${window.screen.width}x${window.screen.height}`,
      view_port: `${window.innerWidth}x${window.innerHeight}`,
      language: navigator.language,
      platform: navigator.platform,
      user_agent: navigator.userAgent
    };

    this.trackEvent({
      action: 'device_info',
      category: 'System',
      customParameters: deviceInfo
    });

    if (this.analytics) {
      setUserProperties(this.analytics, {
        last_resolution: deviceInfo.screen_resolution,
        last_platform: deviceInfo.platform
      });
    }
  }

  /**
   * Enable or disable analytics tracking
   */
  public setEnabled(enabled: boolean): void {
    this.isEnabled = enabled && typeof window !== 'undefined' && 'gtag' in window;
  }
}

// Export a singleton instance
export const analytics = AnalyticsService.getInstance();

// Convenience functions for common tracking scenarios
export const trackPortfolioAction = (action: string, details = {}) => {
  analytics.trackPortfolioEvent(action, details);
};

export const trackPropertyAction = (action: string, details = {}) => {
  analytics.trackPropertyEvent(action, details);
};

export const trackFinancialCalc = (calculationType: string, result?: number, inputs = {}) => {
  analytics.trackFinancialCalculation(calculationType, { result, inputParameters: inputs });
};

export const trackAuth = (action: 'login' | 'logout' | 'signup', method?: string) => {
  analytics.trackAuthEvent(action, method);
};

export const trackAuthStorageIssue = (issueType: 'auth_timeout' | 'persistence_fallback' | 'indexeddb_error', details: {
  elapsedMs?: number;
  errorMessage?: string;
  fallbackType?: string;
} = {}) => {
  analytics.trackAuthStorageIssue(issueType, details);
};

export const trackAI = (action: string, details = {}) => {
  analytics.trackAIAdvice(action, details);
};

export const trackDebt = (action: string, details = {}) => {
  analytics.trackDebtPayoffEvent(action, details);
};

export const trackUI = (elementName: string, action: 'click' | 'open' | 'submit' | 'close', context?: string) => {
  analytics.trackUIInteraction(elementName, action, context);
};

export const trackSession = (pagePath: string, durationSeconds: number) => {
  analytics.trackSessionTime(pagePath, durationSeconds);
};

export const trackFunnel = (funnelName: string, stepName: string, stepNumber: number, details = {}) => {
  analytics.trackFunnelStep(funnelName, stepName, stepNumber, details);
};

export const trackFeedback = (adviceId: string, feedback: 'helpful' | 'unhelpful', comment?: string) => {
  analytics.trackAIFeedback(adviceId, feedback, comment);
};

export const trackAppError = (errorType: string, message: string, stack?: string, fatal = false) => {
  analytics.trackError(errorType, message, stack, fatal);
};

export const logDeviceInfo = () => {
  analytics.captureDeviceInfo();
};
